/* eslint-disable no-undef */
import {Utils} from '@busuu/legacy-core';
import RegistrationService from 'registration/registration-service.js';
import RegistrationTracking from 'tracking/pages/tracking-register.js';
import CaptchaService from 'captcha/captcha-service.js';
import ReferralService from 'referral/referral-service.js';
import FormUtils from 'helpers/form-utils.js';
import ConfigService from 'config/config-service.js';
import {validateTermsAcceptance} from 'registration/registration-busuu.js';
import {AuthenticationGoogleConstant} from '../authentication/authentication-constants';
import {getWidthWithRetry} from '../helpers/html-helpers';

let errorElement;
let GGButton;
let optInToggleElem;
let optInPromotions;

const getTranslation = (key) => {
    return window.BUSUU_TRANSLATIONS[key] || 'NOT FOUND';
};

/**
 * Error Tracking sent after Google API
 * fail or Busuu API fail
 * @param {Object}
 */
const sendTrackingError = (type) => {
    RegistrationTracking.sendRegistrationErrorTracking('GOOGLE', {
        error: type,
    });
};

/**
 * Show the error in case something went wrong
 * @return {String}
 */
const getErrorTextMessage = () => {
    return `${getTranslation('ERROR')} - ${getTranslation('TRY_AGAIN')}`;
};

/**
 * After error is displayed we scroll top
 * to ensure to error is inside the viewport
 * and we enable again the form.
 */
const showError = (type, status) => {
    const code = status ? `[${status} - ${type}]` : `[${type}]`;
    const message = `${getErrorTextMessage(type)}<br><small>${code} - GG</small>`;
    errorElement.innerHTML = message;
    FormUtils.showElement(errorElement);
    window.scrollTo(0, 0);
};

const handleError = ({type = 'UNKNOWN_TYPE', status = null}) => {
    const ignoreError = ['GG_POPUP_CLOSED'].includes(type);
    sendTrackingError(type);
    if (!ignoreError) {
        showError(type, status);
    }
};

const redirect = (url) => {
    window.location.href = url;
};

const onRegistrationSuccess = ({user, registrationResponse}) => {
    const redirectURL = RegistrationService.getAfterRegistrationURL({
        defaultURL: GGButton.getAttribute('data-redirect'),
        registrationResponse,
        user,
    });

    if (registrationResponse.backendRedirection) {
        return redirect(redirectURL);
    }

    return RegistrationTracking.sendRegistrationSuccessTracking('GOOGLE', {
        email_opt_in: optInPromotions,
    }).then(redirect.bind(null, redirectURL));
};

const createGoogleUser = (googleToken, captchaToken) => {
    const getParams = (countryCode) => {
        const getElementValue = (el) => {
            return el.value === 'en' ? 'enc' : el.value;
        };

        const referralCode = ReferralService.getReferralUserCode();
        const perkToken = ReferralService.getPerkToken();
        const timezone = RegistrationService.getTimezoneOffset();
        const defaultInterfaceLang = Utils.getById('register-interface-lang');
        const interfaceLang = RegistrationService.getInterfaceLanguage(defaultInterfaceLang, countryCode);
        const learningLang = getElementValue(Utils.getById('register-learning-lang'));
        optInPromotions = RegistrationService.canOptInPromotions(countryCode, optInToggleElem);

        return {
            token: googleToken,
            learning_language: learningLang,
            interface_language: interfaceLang,
            speaking_language: interfaceLang,
            opt_in_promotions: optInPromotions,
            ...(referralCode && {
                referral_token: referralCode,
            }),
            ...(perkToken && {
                perk_token: perkToken,
            }),
            ...(timezone && {timezone}),
            ...(captchaToken && {
                captcha_token: captchaToken,
            }),
        };
    };

    return ConfigService.getCountryCode()
        .catch(() => {
            return null;
        })
        .then(getParams)
        .then(RegistrationService.createGoogleUser)
        .then(onRegistrationSuccess);
};

const initCaptcha = (googleToken) => {
    const onCaptchaInit = (result) => {
        return createGoogleUser(googleToken, result.token);
    };

    CaptchaService.initializeAndRenderChallenge({
        endpointType: 'register_vendor',
        containerID: 'register-recaptcha',
    })
        .then(onCaptchaInit)
        .catch(handleError);
};

const handleCredentialResponse = (googleResponse = {}) => {
    const googleToken = googleResponse.credential;
    initCaptcha(googleToken);
};

const getSocialsWidth = async () => {
    const socialsWidth = await getWidthWithRetry('register-form-socials');
    const socialsContainer = Utils.getElementByClass('auth-form__socials');
    const computedStyles = window.getComputedStyle(socialsContainer);
    const socialsMaxWidth = parseInt(computedStyles.getPropertyValue('max-width'), 10);

    const socialsWidthValue =
        socialsWidth === 0 ? socialsMaxWidth : Math.min(socialsWidth, socialsMaxWidth) || socialsWidth;

    return socialsWidthValue;
};

const init = () => {
    GGButton = Utils.getById(AuthenticationGoogleConstant.googleSigninButtonName);
    errorElement = Utils.getById('error-generic');
    const userLocal = Utils.getById('register-interface-lang').value;

    window.onload = async () => {
        if (!window.google) return;

        google.accounts.id.initialize({
            client_id: AuthenticationGoogleConstant.clientId,
            context: 'signup',
            ux_mode: 'popup',
            auto_prompt: 'false',
            callback: handleCredentialResponse,
        });

        let socialsWidthValue;
        try {
            socialsWidthValue = await getSocialsWidth();
        } catch (error) {
            socialsWidthValue = 328; // fallback to old hardcoded value
        }

        // Get the overlay from the template
        const overlay = Utils.getById('google-button-overlay');

        // Add click handler to overlay
        overlay.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (!validateTermsAcceptance()) {
                return false;
            }

            // Terms accepted - hide overlay to allow Google button clicks
            overlay.style.display = 'none';
        });

        // Listen for terms checkbox changes
        const termsCheckbox = Utils.getById('register-institution-terms');
        if (termsCheckbox) {
            termsCheckbox.addEventListener('change', () => {
                if (termsCheckbox.checked) {
                    // Terms accepted - hide overlay
                    overlay.style.display = 'none';
                } else {
                    // Terms unchecked - show overlay
                    overlay.style.display = 'block';
                }
            });
        }

        // Render the Google button
        google.accounts.id.renderButton(GGButton, {
            theme: 'outline',
            size: 'large',
            shape: 'pill',
            type: 'standard',
            text: 'continue_with',
            locale: userLocal,
            width: `${socialsWidthValue - 1}px`,
            logo_alignment: 'left',
        });
    };
};

init();
